// Master Apps · Formatação de datas em pt-BR e normalização de busca.

/**
 * Texto comparável para busca: minúsculas, sem acento e com espaços colapsados
 * (pontuação preservada). É o que faz "conciliacao" encontrar "Conciliação" no
 * catálogo — a base do RH e os cadastros vêm cheios de acento, e o usuário
 * digita sem. Mesma normalização usada pelo "Meus apps" para casar responsável.
 */
export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Converte 'YYYY-MM-DD' (ou ISO completo) em 'DD/MM/YYYY'.
 * null / undefined / '' retornam ''. Valores fora do padrão voltam intactos.
 */
export function formatDateBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * Dólar em pt-BR com duas casas: `1.88` → `US$ 1,88`.
 *
 * Montado à mão em vez de `Intl.NumberFormat(..., { style: 'currency' })` porque
 * o formatador de moeda separa o símbolo com ESPAÇO NÃO SEPARÁVEL (U+00A0) e o
 * valor aparece em tabela, tooltip e CSV — texto com NBSP quebra busca, cópia e
 * asserção de teste sem que ninguém veja a diferença na tela. O agrupamento de
 * milhar continua vindo do `toLocaleString` (ponto), como no resto da UI.
 *
 * `null`/`undefined`/valor não finito devolvem '' — quem chama decide o
 * placeholder (no inventário e nos cards é "—", nunca "US$ 0,00", que mentiria
 * sobre um custo desconhecido).
 */
export function formatUsdBR(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  // Zero negativo (`-0`) e resíduo que arredonda para zero (ex.: -0,001, que
  // aparece em subtração de float) sairiam como "US$ -0,00" — sinal de menos num
  // valor nulo faz o leitor procurar um crédito que não existe.
  const bruto = Math.abs(value) < 0.005 ? 0 : value;
  const n = bruto.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `US$ ${n}`;
}

/**
 * Real em pt-BR com duas casas: `9.7` → `R$ 9,70`.
 *
 * Gêmeo de `formatUsdBR` — mesma montagem manual (o `style: 'currency'` do
 * `Intl` separa "R$" do número com espaço NÃO SEPARÁVEL, U+00A0, que quebra
 * busca, cópia e asserção de teste sem aparecer na tela), mesmo tratamento do
 * zero negativo e mesma convenção de devolver '' para valor ausente, deixando o
 * placeholder com quem chama.
 *
 * QUEM USA: a APRESENTAÇÃO, onde dólar não pode aparecer — o valor do Railway é
 * calculado em dólar e convertido pela cotação do dia (ver
 * `apps/api/src/services/cotacao-dolar.ts`). As telas administrativas seguem em
 * `formatUsdBR`, porque lá o assunto é a fatura, que o Railway emite em dólar.
 */
export function formatBrlBR(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const bruto = Math.abs(value) < 0.005 ? 0 : value;
  const n = bruto.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `R$ ${n}`;
}

/**
 * Data/hora ISO em 'DD/MM/AAAA HH:MM' no fuso do navegador — usada no rodapé de
 * "atualizado em" dos custos do Railway. Valor inválido devolve ''.
 */
export function formatDateTimeBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/**
 * Converte 'YYYY-MM-DD' (ou ISO completo) em 'DD/MM'.
 * null / undefined / '' retornam ''. Valores fora do padrão voltam intactos.
 */
export function formatDateShortBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}`;
}
